import { lstr } from "./localization";

// Strength (0..1, from practice) of one direction of a word as a level.
// Thresholds match api/src/srs Strength: 0.3 is about 3 days of memory, 0.6
// about two weeks.
type StrengthLevel = "unpracticed" | "weak" | "learning" | "strong";

function strengthLevel(practiced: boolean, strength: number): StrengthLevel {
  if (!practiced || strength <= 0) return "unpracticed";
  if (strength < 0.3) return "weak";
  if (strength < 0.6) return "learning";
  return "strong";
}

const STRENGTH_CLASS: Record<StrengthLevel, string> = {
  unpracticed: "bg-slate-300 dark:bg-slate-600",
  weak: "bg-rose-500",
  learning: "bg-amber-400",
  strong: "bg-emerald-500",
};

// StrengthDot shows how well a word is known in both directions at a glance:
// the left half is recognition (word to meaning), the right half production
// (meaning to word).
export function StrengthDot({
  l,
  practiced,
  recognizeStrength,
  produceStrength,
}: {
  l: string;
  practiced: boolean;
  recognizeStrength: number;
  produceStrength: number;
}) {
  const strings = lstr(l);
  const recognize = strengthLevel(practiced, recognizeStrength);
  const produce = strengthLevel(practiced, produceStrength);
  const label = `${strings.strength_recognize}: ${strings[`strength_${recognize}`]}, ${strings.strength_produce}: ${strings[`strength_${produce}`]}`;
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className="inline-flex shrink-0 w-3 h-3 rounded-full overflow-hidden"
    >
      <span className={`w-1/2 h-full ${STRENGTH_CLASS[recognize]}`} />
      <span className={`w-1/2 h-full ${STRENGTH_CLASS[produce]}`} />
    </span>
  );
}

// StrengthLegend explains the dots in one line: what the colors mean, and
// that the two halves are recognition and recall.
export function StrengthLegend({ l }: { l: string }) {
  const strings = lstr(l);
  const levels: StrengthLevel[] = ["strong", "learning", "weak", "unpracticed"];
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-secondary-text">
      {levels.map((level) => (
        <span key={level} className="inline-flex items-center gap-1.5">
          <span className={`h-2 w-2 rounded-full ${STRENGTH_CLASS[level]}`} />
          {strings[`strength_${level}`]}
        </span>
      ))}
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-flex h-2.5 w-2.5 overflow-hidden rounded-full">
          <span className="h-full w-1/2 bg-emerald-500" />
          <span className="h-full w-1/2 bg-amber-400" />
        </span>
        {strings.strength_recognize} / {strings.strength_produce}
      </span>
    </div>
  );
}
